import React from "react";

import { getConfidenceTier, TIER_COLORS } from "../../../shared/components/ConfidenceBadge";

interface ConfidenceExplainerProps {
  /** Whether the panel is currently visible. */
  open: boolean;
  /** Callback to close the panel. */
  onClose: () => void;
  /** The actual confidence value (0–1) for this card. */
  confidence?: number | null;
}

/**
 * Compact dropdown explainer that appears directly below the confidence
 * badge. Semi-transparent with backdrop blur for a seamless glass feel.
 *
 * The outer wrapper is always present for CSS transitions; content is
 * conditionally rendered so queryByText queries return null when closed.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function ConfidenceExplainer({ open, onClose, confidence }: ConfidenceExplainerProps): JSX.Element {
  const pct = confidence != null ? Math.round(confidence * 100) : 65;
  const opposite = 100 - pct;
  const tierColor = TIER_COLORS[getConfidenceTier(confidence ?? null)][0];

  return (
    <div
      className={`confidence-explainer${open ? " confidence-explainer--open" : ""}`}
      role="tooltip"
      aria-hidden={!open}
    >
      {open && (
        <div className="confidence-explainer-inner">
          <div className="confidence-explainer-header">
            <strong className="confidence-explainer-title">Understanding Confidence</strong>
            <button
              className="confidence-explainer-close"
              aria-label="Close explanation"
              onClick={onClose}
              type="button"
            >✕</button>
          </div>
          <dl className="confidence-explainer-list">
            <dt>What is this number?</dt>
            <dd>
              It&apos;s a win probability. <strong style={{ color: tierColor }}>{pct}%</strong> means the
              predicted winner wins about {pct} out of 100 similar games.
            </dd>

            <dt>Where does it come from?</dt>
            <dd>
              Our model looks at pitching matchups, batting stats, recent form,
              and historical patterns, then calibrates the result so the
              percentages match real-world win rates.
            </dd>

            <dt>Does higher always mean a sure thing?</dt>
            <dd>
              No. Even at <strong style={{ color: tierColor }}>{pct}%</strong>, the other team still wins around {opposite}% of the time.
              Think of it as an edge, not a guarantee.
            </dd>
          </dl>
        </div>
      )}
    </div>
  );
}
